import { useEffect, useState } from "react";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api, type AwaitingReply, type Throughput } from "../lib/api";

/** People who wrote to us and got nothing back, and what the send pipe is
 *  doing right now.
 *
 *  Both halves existed only in the database before this. A reply marks the
 *  contact as having responded and cancels anything still queued for them,
 *  and then stops: tagging happens through campaign transition rules, so a
 *  reply from somebody not in a campaign routes nowhere at all. A one-off
 *  blast produces exactly those. On the day this was written six people had
 *  asked for a call and no screen in the product would have shown them.
 */
export function Replies() {
  const [rows, setRows] = useState<AwaitingReply[]>([]);
  const [pipe, setPipe] = useState<Throughput | null>(null);
  const [days, setDays] = useState(30);
  const [onlyQuestions, setOnlyQuestions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .awaitingReply(days, 200)
      .then(setRows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    const load = () => api.throughput().then(setPipe).catch(() => setPipe(null));
    load();
    const t = setInterval(() => {
      if (!document.hidden) load();
    }, 10000);
    return () => clearInterval(t);
  }, []);

  const shown = onlyQuestions ? rows.filter((r) => r.looks_like_a_question) : rows;
  const questions = rows.filter((r) => r.looks_like_a_question).length;

  const ago = (hours: number) =>
    hours < 1
      ? `${Math.round(hours * 60)}m`
      : hours < 48
        ? `${Math.round(hours)}h`
        : `${Math.round(hours / 24)}d`;

  return (
    <>
      <ErrorBanner error={error} />

      <div className="row">
        <h2 style={{ fontSize: 19 }}>Replies &amp; sending</h2>
        <div className="sp" />
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            className={`btn s${days === d ? " p" : ""}`}
            onClick={() => setDays(d)}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* What the pipe is doing. The capability figure is measured from
          messages that actually left, not read from a setting - nothing in
          the send path enforces a configured rate, so a number from settings
          would be a guess presented as a fact. */}
      <Card title="Sending right now">
        {!pipe ? (
          <div className="dim">…</div>
        ) : (
          <>
            <div className="tiles">
              <div className="tile">
                <span className="lab">Sent in the last hour</span>
                <span className="v">{fmt.n(pipe.sent_last_hour)}</span>
              </div>
              <div className={`tile ${pipe.due_now > 0 ? "ok" : ""}`}>
                <span className="lab">Due now, not yet sent</span>
                <span className="v">{fmt.n(pipe.due_now)}</span>
              </div>
              <div className="tile">
                <span className="lab">In flight</span>
                <span className="v">{fmt.n(pipe.claimed)}</span>
              </div>
              <div className="tile">
                {/* Held texts come out of both terms: they are neither due nor
                    waiting for a window, they are waiting for someone to
                    un-pause a campaign. Subtracting only due_now would count
                    them here and overstate what is about to go out. */}
                <span className="lab">Waiting for their window</span>
                <span className="v">
                  {fmt.n(pipe.pending - pipe.due_now - pipe.due_but_held)}
                </span>
              </div>
              {pipe.due_but_held > 0 && (
                <div className="tile">
                  <span className="lab">Waiting on a paused campaign</span>
                  <span className="v">{fmt.n(pipe.due_but_held)}</span>
                </div>
              )}
            </div>
            <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>
              Best minute measured{" "}
              <b className="mono">{fmt.n(pipe.peak_per_minute)}/min</b>, and about{" "}
              <b className="mono">{fmt.n(pipe.sustained_per_minute)}/min</b>{" "}
              sustained over the last {pipe.measured_over_days} days — roughly{" "}
              {fmt.n(pipe.sustained_per_minute * 60)} an hour. That is set by how
              many send workers run, not by any rate in settings.
            </div>
          </>
        )}
      </Card>

      <Card
        title={
          <div className="row" style={{ gap: 9 }}>
            <h3>Waiting on an answer</h3>
            {questions > 0 && <Pill tone="ba">{questions} asked something</Pill>}
          </div>
        }
        actions={
          <button
            className={`btn s${onlyQuestions ? " p" : ""}`}
            onClick={() => setOnlyQuestions((v) => !v)}
          >
            {onlyQuestions ? "Showing questions" : "Only questions"}
          </button>
        }
      >
        <div className="dim" style={{ fontSize: 11.5, marginBottom: 8 }}>
          Their message is the last one in the thread — nobody has written back.
          Anyone who has opted out or been suppressed is left out, and so are
          broadcasts that reached us labelled as replies.
        </div>

        {loading && <div className="dim">Looking…</div>}
        {!loading && shown.length === 0 && (
          <div className="empty">Nobody is waiting. Good.</div>
        )}

        {shown.map((r) => (
          <div
            key={`${r.contact_id}-${r.at}`}
            className="row w"
            style={{
              gap: 10,
              padding: "7px 0",
              borderTop: "1px solid var(--line2)",
              alignItems: "flex-start",
            }}
          >
            <span className="mono dim" style={{ fontSize: 11, width: 40 }}>
              {ago(r.hours_waiting)}
            </span>
            <a href={`/contacts/${r.contact_id}`} style={{ minWidth: 150 }}>
              {r.name || r.phone_e164}
            </a>
            <span className="mono dim" style={{ fontSize: 11.5, width: 120 }}>
              {r.phone_e164}
            </span>
            <span style={{ flex: 1, minWidth: 220 }}>
              {r.looks_like_a_question && (
                <Pill tone="ba">asked</Pill>
              )}{" "}
              {r.body || <span className="dim">(no text)</span>}
            </span>
          </div>
        ))}
      </Card>
    </>
  );
}
